"use client";

import { Field } from "@/components/tool-kit/primitives";
import { INPUT_MONO, SELECT } from "@/components/tool-kit/styles";
import type { UnitDef } from "@/lib/tool-kit/physics-waves/units";

/**
 * A number box with its unit menu beside it — the building block of every physics
 * calculator. The value stays text (the widget parses it with `parseNumberInput`).
 * `unitLabel` is the accessible name of the menu, already filled in by the widget.
 */
export function QuantityField({
  id,
  label,
  value,
  onChange,
  unit,
  onUnitChange,
  units,
  unitLabel,
  hint,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  unit: string;
  onUnitChange: (id: string) => void;
  units: readonly UnitDef[];
  unitLabel: string;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <input
          className={INPUT_MONO}
          id={id}
          inputMode="decimal"
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          value={value}
        />
        <select
          aria-label={unitLabel}
          className={`${SELECT} w-auto`}
          onChange={(event) => onUnitChange(event.target.value)}
          value={unit}
        >
          {units.map((option) => (
            <option key={option.id} value={option.id}>
              {option.symbol}
            </option>
          ))}
        </select>
      </div>
    </Field>
  );
}

/** The numbered working under a result: one line per step, in a quiet box. */
export function WorkingSteps({
  heading,
  lines,
}: {
  heading: string;
  lines: readonly string[];
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm">
      <p className="font-medium font-sans text-foreground">{heading}</p>
      <ol className="space-y-1">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ol>
    </div>
  );
}
